import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';

import { Edit } from '@mui/icons-material';
import { Box, IconButton, Stack, TextField } from '@mui/material';

import ErrorSnackbar from 'domains/core/components/Snackbars/ErrorSnackbar';
import SuccessSnackbar from 'domains/core/components/Snackbars/SuccessSnackbar';
import TruncatedTextWithTooltip from 'domains/core/components/TruncatedTextWithTooltip';
import usePatchUserDisplayName from 'hooks/mutations/usePatchUserDisplayName';

import {
    usersTableInlineEditableTextFieldSx,
    usersTableInteractiveCellStackSx,
    usersTableInteractiveCellTriggerIconButtonSx,
    usersTableInteractiveCellValueBoxSx,
} from '../usersTableInteractiveCellStyles';

import type { AccessCentreUser, PatchUserDisplayNameApiResponse } from 'domains/accessCentre/types';

export type UsersTableNameCellProps = {
    user: AccessCentreUser;
    allowInlineEdit?: boolean;
};

/** Display name exactly as returned by GET /users (`name`), without email fallbacks. */
const getDisplayUserName = (user: AccessCentreUser): string => user.name ?? '';

const UsersTableNameCell = ({ user, allowInlineEdit = false }: UsersTableNameCellProps) => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [draftName, setDraftName] = useState(() => getDisplayUserName(user));
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const displayLabel = getDisplayUserName(user);

    const {
        mutate: patchDisplayName,
        isError: isPatchError,
        error: patchError,
        isLoading: isPatchLoading,
        reset: resetPatchMutation,
    } = usePatchUserDisplayName(user.id);

    useEffect(() => {
        if (!isEditing) {
            setDraftName(getDisplayUserName(user));
        }
    }, [user, isEditing]);

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isEditing]);

    if (!allowInlineEdit) {
        return <TruncatedTextWithTooltip value={displayLabel} />;
    }

    const handleStartEdit = () => {
        resetPatchMutation();
        setSuccessMessage(null);
        setDraftName(getDisplayUserName(user));
        setIsEditing(true);
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setDraftName(getDisplayUserName(user));
        resetPatchMutation();
    };

    const submitNameChange = () => {
        const trimmed = draftName.trim();
        if (trimmed.length === 0) {
            handleCancelEdit();
            return;
        }
        if (trimmed === (user.name ?? '').trim()) {
            setIsEditing(false);
            return;
        }
        patchDisplayName(
            { name: trimmed },
            {
                onSuccess: (data: PatchUserDisplayNameApiResponse) => {
                    const updated = data?.user;
                    const display = updated?.name ?? trimmed;
                    setSuccessMessage(`Display name updated to ${display}`);
                    setIsEditing(false);
                },
            }
        );
    };

    const handleBlur = () => {
        if (isPatchLoading) return;
        submitNameChange();
    };

    const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            handleCancelEdit();
        }
        if (e.key === 'Enter') {
            e.preventDefault();
            submitNameChange();
        }
    };

    return (
        <>
            {successMessage && <SuccessSnackbar key={successMessage} successMessage={successMessage} />}
            {isPatchError && patchError && <ErrorSnackbar errorMessage={patchError.message ?? ''} />}
            {isEditing ? (
                <TextField
                    inputRef={inputRef}
                    size="small"
                    fullWidth
                    value={draftName}
                    onChange={(e) => setDraftName(e.target.value)}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    disabled={isPatchLoading}
                    inputProps={{
                        'aria-label': 'Edit user display name',
                        maxLength: 255,
                        'data-testid': 'access-center-user-name-input',
                    }}
                    sx={usersTableInlineEditableTextFieldSx}
                />
            ) : (
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.5}
                    justifyContent="flex-start"
                    sx={{
                        ...usersTableInteractiveCellStackSx,
                        '&:hover .access-center-user-name-edit-trigger': { opacity: 1 },
                    }}
                >
                    <Box sx={usersTableInteractiveCellValueBoxSx}>
                        <TruncatedTextWithTooltip value={displayLabel} maxWidth="100%" />
                    </Box>
                    <IconButton
                        type="button"
                        size="small"
                        color="inherit"
                        className="access-center-user-name-edit-trigger"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleStartEdit();
                        }}
                        disabled={isPatchLoading}
                        aria-label={`Edit display name for ${displayLabel || user.email || 'user'}`}
                        data-testid="access-center-user-name-edit-trigger"
                        sx={{
                            ...usersTableInteractiveCellTriggerIconButtonSx,
                            opacity: 0,
                            transition: (theme) =>
                                theme.transitions.create('opacity', { duration: theme.transitions.duration.shorter }),
                            '&:focus-visible': { opacity: 1 },
                        }}
                    >
                        <Edit fontSize="inherit" />
                    </IconButton>
                </Stack>
            )}
        </>
    );
};

export default UsersTableNameCell;
